import React, { Component } from 'react'

class EditStepNameForm extends Component {

    constructor(props) {
        super()
        this.stepTitleRef = React.createRef();    
        this.state = {
            title: props.stepTitle
        }
    }

    componentDidMount() {
        this.setFocus()
        this.setupClickListener();
    }

    componentWillUnmount() {
        this.props.clearFormErrors()
        this.cleanupClickListener();
    }

    setupClickListener = () => {
        // Use the same global event delegation pattern as the Editor
        if (!window._stepTitleEditorRegistry) {
            window._stepTitleEditorRegistry = new Map();
            // Set up global click listener only once
            const globalClickHandler = (event) => {
                console.log('[Global Step Title] Click detected, checking editors...');
                
                // Find which tab panels are in the event path
                const eventPath = event.composedPath ? event.composedPath() : [];
                const tabPanelsInPath = eventPath.filter(element => 
                    element.classList && element.classList.contains('tabPageElement')
                );
                
                console.log('[Global Step Title] Tab panels in click path:', tabPanelsInPath.map(tp => tp.id));
                
                // Only call handlers for editors that are NOT in the clicked tab panel
                window._stepTitleEditorRegistry.forEach((editorInfo, editorId) => {
                    if (editorInfo.handler && editorInfo.isActive) {
                        const isClickInEditorTab = tabPanelsInPath.some(tabPanel => 
                            tabPanel === editorInfo.tabPanel
                        );
                        
                        if (!isClickInEditorTab) {
                            console.log(`[Global Step Title] Click outside editor ${editorId}, calling handler`);
                            editorInfo.handler(event);
                        } else {
                            console.log(`[Global Step Title] Click within editor ${editorId} tab, ignoring`);
                        }
                    }
                });
            };
            
            const container = document.getElementById("LeftRightAuthorContainer");
            if (container) {
                container.addEventListener("click", globalClickHandler, true);
                console.log('[Global Step Title] Global click listener established');
            }
        }
        
        // Find this editor's tab panel
        let myTabPanel = null;
        try {
            if (this.stepTitleRef.current) {
                let current = this.stepTitleRef.current;
                while (current && current.parentElement) {
                    current = current.parentElement;
                    if (current.classList && current.classList.contains('tabPageElement')) {
                        myTabPanel = current;
                        break;
                    }
                }
            }
        } catch (error) {
            console.log(`[Step Title Editor] Error finding tab panel:`, error);
        }
        
        // Generate unique ID for this editor instance
        const editorInstanceId = `stepTitle_${Date.now()}_${Math.random()}`;
        this.editorInstanceId = editorInstanceId;
        
        // Register this editor
        window._stepTitleEditorRegistry.set(editorInstanceId, {
            handler: this.handleClickOutside,
            isActive: true,
            tabPanel: myTabPanel
        });
        
        console.log(`[Step Title Editor ${editorInstanceId}] Registered in global registry`);
        console.log(`[Step Title Editor ${editorInstanceId}] My tab panel:`, myTabPanel?.id);
        console.log(`[Global Step Title] Total active editors:`, window._stepTitleEditorRegistry.size);
    }

    cleanupClickListener = () => {
        // Restore tab indexes
        this.restoreTabIndexes();
        
        // Remove all event listeners
        const input = document.getElementById("naming-container");
        if (input) {
            input.removeEventListener('blur', this.handleBlur);
            input.removeEventListener('focus', this.handleFocus);
            input.removeEventListener('click', this.handleInputClick);
            input.removeEventListener('keydown', this.handleInputKeydown);
        }
        
        // Unregister from global registry
        if (window._stepTitleEditorRegistry && this.editorInstanceId) {
            window._stepTitleEditorRegistry.delete(this.editorInstanceId);
            console.log(`[Step Title Editor ${this.editorInstanceId}] Unregistered from global registry`);
            console.log(`[Global Step Title] Remaining editors:`, window._stepTitleEditorRegistry.size);
        }
    }

    handleClickOutside = (event) => {
        console.log(`[Step Title Editor ${this.editorInstanceId}] handleClickOutside triggered`);
        if (this.stepTitleRef && this.stepTitleRef.current && !this.stepTitleRef.current.contains(event.target)) {
            console.log(`[Step Title Editor ${this.editorInstanceId}] Click outside, canceling edit`);
            this.props.cancel(event);
        } else {
            console.log(`[Step Title Editor ${this.editorInstanceId}] Click inside editor, ignoring`);
        }
    }

    setFocus() {
        let input = document.getElementById("naming-container");
        if (input) {
            input.focus();
            console.log(`[Step Title Editor ${this.editorInstanceId}] Focus set on input`);
            
            // Add focus protection like we did for TinyMCE
            this.setupFocusProtection(input);
        }
    }

    setupFocusProtection = (input) => {
        // Temporarily disable tab focusing on tab headers while editing
        const tabHeaders = document.querySelectorAll('.react-tabs__tab');
        tabHeaders.forEach(tab => {
            if (tab.getAttribute('tabindex') !== '-1') {
                tab.setAttribute('data-original-tabindex', tab.getAttribute('tabindex') || '0');
                tab.setAttribute('tabindex', '-1');
                console.log(`[Step Title Editor ${this.editorInstanceId}] Disabled tabindex for tab:`, tab.id);
            }
        });

        // Add focus/blur event listeners
        input.addEventListener('blur', this.handleBlur);
        input.addEventListener('focus', this.handleFocus);
        
        // Prevent event bubbling that might interfere with focus
        input.addEventListener('click', this.handleInputClick);
        input.addEventListener('keydown', this.handleInputKeydown);
    }

    handleFocus = (e) => {
        console.log(`[Step Title Editor ${this.editorInstanceId}] Input gained focus`);
    }

    handleBlur = (e) => {
        console.log(`[Step Title Editor ${this.editorInstanceId}] Input lost focus, relatedTarget:`, e.relatedTarget);
        
        // Check if focus is moving to a tab header inappropriately
        const isMovingToTabHeader = e.relatedTarget && e.relatedTarget.classList.contains('react-tabs__tab');
        
        if (isMovingToTabHeader) {
            console.log(`[Step Title Editor ${this.editorInstanceId}] Focus moving to tab header, refocusing input`);
            setTimeout(() => {
                const input = document.getElementById("naming-container");
                if (input && !input.disabled) {
                    input.focus();
                }
            }, 10);
        }
    }

    handleInputClick = (e) => {
        console.log(`[Step Title Editor ${this.editorInstanceId}] Input clicked, preventing bubbling`);
        e.stopPropagation();
    }

    handleInputKeydown = (e) => {
        console.log(`[Step Title Editor ${this.editorInstanceId}] Key pressed:`, e.key);
        e.stopPropagation();
    }

    restoreTabIndexes = () => {
        // Re-enable tab focusing when editor closes
        const tabHeaders = document.querySelectorAll('.react-tabs__tab');
        tabHeaders.forEach(tab => {
            const originalTabIndex = tab.getAttribute('data-original-tabindex');
            if (originalTabIndex !== null) {
                tab.setAttribute('tabindex', originalTabIndex);
                tab.removeAttribute('data-original-tabindex');
                console.log(`[Step Title Editor ${this.editorInstanceId}] Restored tabindex for tab:`, tab.id);
            }
        });
    }

    renderErrors = (error) => {
        return (
            <ul className="flex-centered shake-on-error" id="form-errors">
                {error}
            </ul>
        );
    }

    handleChange = (e) => {
        if (this.props.errors.length > 0) {
            this.props.clearFormErrors()
        }
        this.setState({ title: e.target.value })
    }

    handleSubmit = (e) => {
        console.log("clikced on name submit")
        e.preventDefault()
        if (!this.state.title) {
            return this.props.submitFormError("Step Names Cannot Be Blank")
        }

        if (this.props.stepTitle === this.state.title) {
            this.props.cancel()
        }

        this.props.submitStepNameEdit(e, this.state.title)
    }

    render() {
        return <form onSubmit={this.handleSubmit} id="edit-step-name-form" className="flex" ref={this.stepTitleRef}>
            <input
                id="naming-container"
                type="text"
                placeholder="Name Your Step"
                value={this.state.title}
                onChange={this.handleChange}
                style={{width: '90%'}}
            />
            {this.props.errors.length > 0 ? this.renderErrors(this.props.errors) : null}
            <div id="new-step-action-buttons">
                <span style={{ background: "lightgrey" }} onClick={(event) => this.props.cancel(event)}>Cancel</span>
                <span onClick={(event)=>this.handleSubmit(event)} style={{ background: "#3d7ce2", color: "white" }}>Submit</span>
            </div>
        </form> 
    }
}

export default EditStepNameForm